// Source-of-truth route catalog used by the AppShell, RouteGuards, and visual-proof spec.

import {
  ROUTE_PERMISSIONS,
  ROLE_IDS,
  PERSONA_TO_USER_ID,
  PERSONA_DEFAULT_ROUTE,
  hasAnyRole,
} from './rbac.js';

export const ROUTES = Object.freeze([
  { path: '/', label: 'App Root', section: 'auth', auth: false },
  { path: '/login', label: 'PRIME Sign-in', section: 'auth', auth: false },
  { path: '/request-access', label: 'PMHub Request Access', section: 'auth', auth: false },
  { path: '/change-password', label: 'Change Password', section: 'auth', auth: true },
  { path: '/access/request', label: 'Request Access', section: 'auth', auth: false },
  { path: '/access/request/:id', label: 'Access Request Detail', section: 'auth', auth: true },
  { path: '/access/status', label: 'Access Status', section: 'auth', auth: true },
  { path: '/home', label: 'Home', section: 'home', auth: true },
  { path: '/not-authorized', label: 'Not Authorized', section: 'auth', auth: true },

  {
    path: '/executive',
    label: 'Executive Command Center',
    section: 'executive',
    auth: true,
    hero: true,
    artboard: 'executive_command_center_3',
  },
  {
    path: '/analytics/mission-impact',
    label: 'Mission Impact',
    section: 'executive',
    auth: true,
    artboard: 'portfolio_analytics_mission_impact',
  },
  { path: '/portfolios', label: 'Portfolios', section: 'executive', auth: true },
  { path: '/portfolios/:id', label: 'Portfolio Detail', section: 'executive', auth: true },
  { path: '/programs', label: 'Programs', section: 'executive', auth: true },
  { path: '/programs/:id', label: 'Program Detail', section: 'executive', auth: true },
  { path: '/projects', label: 'Projects', section: 'executive', auth: true },
  { path: '/projects/:id', label: 'Project Detail', section: 'executive', auth: true },
  { path: '/project-factory', label: 'Project Factory', section: 'executive', auth: true },
  {
    path: '/project-factory/review',
    label: 'Project Factory Review',
    section: 'executive',
    auth: true,
  },
  {
    path: '/project-factory/requests/new',
    label: 'Project Factory Request Intake',
    section: 'executive',
    auth: true,
  },
  {
    path: '/project-factory/requests/:id',
    label: 'Project Factory Request Detail',
    section: 'executive',
    auth: true,
  },
  { path: '/loe-dashboard', label: 'LOE Dashboard', section: 'executive', auth: true },
  { path: '/analytics/loes', label: 'LOE Dashboard', section: 'executive', auth: true },
  { path: '/analytics/loes/:id', label: 'LOE Detail', section: 'executive', auth: true },
  { path: '/actions', label: 'Action Center', section: 'delivery', auth: true },
  { path: '/actions/:id', label: 'Action Detail', section: 'delivery', auth: true },
  { path: '/meetings', label: 'Meeting Manager', section: 'delivery', auth: true },
  { path: '/meetings/:id', label: 'Meeting Detail', section: 'delivery', auth: true },
  {
    path: '/meetings/:id/convert-to-time',
    label: 'Meeting to Time Conversion',
    section: 'delivery',
    auth: true,
  },
  { path: '/knowledge', label: 'Knowledge Library', section: 'delivery', auth: true },
  {
    path: '/knowledge/lessons-learned',
    label: 'Lessons Learned Library',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/knowledge/artifacts/:id',
    label: 'Knowledge Artifact Detail',
    section: 'delivery',
    auth: true,
  },
  { path: '/resources', label: 'Resource Command Center', section: 'delivery', auth: true },
  {
    path: '/analytics/resources',
    label: 'Resource Command Center',
    section: 'delivery',
    auth: true,
  },
  { path: '/resources/scheduler', label: 'Resource Scheduler', section: 'delivery', auth: true },
  { path: '/resources/utilization', label: 'Utilization Report', section: 'delivery', auth: true },
  { path: '/resources/skills', label: 'Skills Matrix', section: 'delivery', auth: true },
  {
    path: '/resources/allocations/:id',
    label: 'Allocation Conflict Detail',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/resources/assignments/new',
    label: 'Assignment Wizard',
    section: 'delivery',
    auth: true,
  },
  { path: '/timesheets', label: 'Timesheet Home', section: 'delivery', auth: true },
  {
    path: '/timesheets/current',
    label: 'Current Weekly Timesheet',
    section: 'delivery',
    auth: true,
  },
  { path: '/timesheets/:id', label: 'Timesheet Detail', section: 'delivery', auth: true },
  { path: '/governance', label: 'Governance Command Center', section: 'delivery', auth: true },
  { path: '/governance/risks', label: 'Risk Register', section: 'delivery', auth: true },
  { path: '/governance/issues', label: 'Issue Log', section: 'delivery', auth: true },
  { path: '/governance/changes', label: 'Change Requests', section: 'delivery', auth: true },
  { path: '/governance/evm', label: 'PMO / EVM Dashboard', section: 'delivery', auth: true },

  {
    path: '/delivery',
    label: 'Delivery Workspace',
    section: 'delivery',
    auth: true,
    hero: true,
    artboard: 'integrated_delivery_workspace_1',
  },
  { path: '/work-items/:id', label: 'Work Item Detail', section: 'delivery', auth: true },
  {
    path: '/work-items/:id/log-time',
    label: 'Work Item Time Entry',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/roadmap',
    label: 'Strategic Roadmap',
    section: 'delivery',
    auth: true,
    artboard: 'roadmap_gantt_workspace_2',
  },
  {
    path: '/financials',
    label: 'Financial & Lifecycle',
    section: 'delivery',
    auth: true,
    artboard: 'financial_lifecycle_manager',
  },
  {
    path: '/financials/adjustments',
    label: 'Forecast Adjustments',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/financials/funding-sources',
    label: 'Funding Sources',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/lifecycle',
    label: 'Portfolio Lifecycle',
    section: 'delivery',
    auth: true,
    artboard: 'portfolio_lifecycle_management',
  },
  {
    path: '/decision-support',
    label: 'Decision Support',
    section: 'delivery',
    auth: true,
    artboard: 'strategic_analysis_decision_support',
  },
  { path: '/market-research', label: 'Market Research', section: 'delivery', auth: true },
  { path: '/projects/:id/closeout', label: 'Project Closeout', section: 'delivery', auth: true },
  { path: '/rapids', label: 'RAPID Command Center', section: 'delivery', auth: true },
  { path: '/rapids/:id', label: 'RAPID Detail', section: 'delivery', auth: true },
  {
    path: '/projects/:projectId/gate-review/:gateId',
    label: 'Project Gate Review',
    section: 'delivery',
    auth: true,
  },

  {
    path: '/ai/orchestration',
    label: 'AI Orchestration',
    section: 'ai',
    auth: true,
    artboard: 'ai_orchestration_hub',
  },
  { path: '/ai/runs/:id', label: 'AI Run Detail', section: 'ai', auth: true },
  { path: '/ai/ask', label: 'Ask PRIME', section: 'ai', auth: true },

  {
    path: '/admin/data',
    label: 'Data Integration',
    section: 'admin',
    auth: true,
    artboard: 'data_integration_rbac_settings',
  },
  { path: '/admin/custom-fields', label: 'Configuration', section: 'admin', auth: true },
  { path: '/admin/forms', label: 'Form Builder', section: 'admin', auth: true },
  { path: '/admin/rubrics', label: 'Rubric Admin', section: 'admin', auth: true },
  { path: '/admin/report-templates', label: 'Report Registry', section: 'admin', auth: true },
  { path: '/admin/config/report-registry', label: 'Report Registry', section: 'admin', auth: true },
  { path: '/admin/ingestion', label: 'Ingestion Center', section: 'admin', auth: true },
  { path: '/admin/ingestion/:jobId', label: 'Ingestion Job Detail', section: 'admin', auth: true },
  { path: '/admin/lineage', label: 'Lineage Explorer', section: 'admin', auth: true },
  { path: '/admin/data-integrations', label: 'Data Integrations', section: 'admin', auth: true },
  { path: '/admin/data/connectors/:id', label: 'Connector Detail', section: 'admin', auth: true },
  { path: '/admin/rbac', label: 'RBAC Admin', section: 'admin', auth: true },
  { path: '/admin/site-access', label: 'CAC Approval', section: 'admin', auth: true },
  { path: '/admin/access-requests', label: 'Access Reviews', section: 'admin', auth: true },
  {
    path: '/admin/access-requests/:id',
    label: 'Access Review Detail',
    section: 'admin',
    auth: true,
  },
  { path: '/admin/users', label: 'Users', section: 'admin', auth: true },
  { path: '/admin/authentication', label: 'Authentication Controls', section: 'admin', auth: true },
  { path: '/admin/audit', label: 'Audit Log', section: 'admin', auth: true },
  { path: '/admin/guide-results', label: 'PM Guide Results', section: 'admin', auth: true },
  { path: '/admin/activity', label: 'Activity Reports', section: 'admin', auth: true },
  {
    path: '/admin/activity/reports/:id',
    label: 'Activity Report Viewer',
    section: 'admin',
    auth: true,
  },
  { path: '/admin/compliance', label: 'Compliance Readiness', section: 'admin', auth: true },

  { path: '/notifications', label: 'Notifications', section: 'me', auth: true },
  { path: '/activity', label: 'My Activity', section: 'me', auth: true },
  { path: '/saved-views', label: 'Saved Views', section: 'me', auth: true },
  { path: '/search', label: 'Search', section: 'me', auth: true },
  { path: '/feedback', label: 'My Feedback', section: 'delivery', auth: true },
  { path: '/feedback/queue', label: 'Feedback Queue', section: 'delivery', auth: true },
  // Typed help/intake framework, commit 2. Declared BEFORE '/feedback/:packageId'
  // so the literal 'support' segment is never read as a package id by a consumer
  // that walks this catalog in order.
  { path: '/feedback/support/new', label: 'Report a Problem', section: 'delivery', auth: true },
  {
    path: '/feedback/support/:intakeId',
    label: 'Support Ticket Draft',
    section: 'delivery',
    auth: true,
  },
  // Typed help/intake framework, commit 4. Same reason as 'support' above: the
  // literal 'request' segment is declared BEFORE '/feedback/:packageId' so a
  // consumer walking this catalog in order never reads it as a package id.
  {
    path: '/feedback/request/new',
    label: 'Request a Feature or Service',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/feedback/request/:intakeId',
    label: 'Feature or Service Request Draft',
    section: 'delivery',
    auth: true,
  },
  { path: '/feedback/workspace', label: 'Feedback Workspace', section: 'delivery', auth: true },
  {
    path: '/feedback/submitted/:packageId',
    label: 'Feedback Sent Confirmation',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/feedback/:packageId/edit',
    label: 'Feedback Draft Workspace',
    section: 'delivery',
    auth: true,
  },
  {
    path: '/feedback/:packageId',
    label: 'Feedback History Replay',
    section: 'delivery',
    auth: true,
  },
  { path: '/uat', label: 'UAT Dashboard', section: 'delivery', auth: true },
  { path: '/products', label: 'Briefing Product Factory', section: 'me', auth: true },
  { path: '/briefings/:id', label: 'Briefing Detail', section: 'me', auth: true },
  { path: '/status-packs', label: 'Status Pack Library', section: 'me', auth: true },
  { path: '/status-packs/:id', label: 'Status Pack Detail', section: 'me', auth: true },
  { path: '/status/:shareId', label: 'Read-only Status Portal', section: 'me', auth: false },
  { path: '/exports', label: 'Exports', section: 'me', auth: true },
  { path: '/me', label: 'My Profile', section: 'me', auth: true },
  { path: '/settings', label: 'Settings', section: 'me', auth: true },
  { path: '/help', label: 'Help & getting started', section: 'me', auth: true },
  { path: '/help/glossary', label: 'Glossary', section: 'me', auth: true },
  { path: '/help/capability-map', label: 'Capability map', section: 'me', auth: true },
  // Typed help/intake framework, commit 5. Help Q&A: the reporter asks a
  // question, PRIME answers it from its knowledge base where it can, and hands
  // the question to a person where it cannot. Not a rail item — it is reached
  // from the Help Center card and from the feedback launcher.
  { path: '/help/questions', label: 'Ask a question', section: 'me', auth: true },
  { path: '*', label: 'Not Found', section: 'auth', auth: true },
]);

const KNOWN_ROUTE_PATHS = new Set(ROUTES.map((route) => route.path));
const PERSONA_ROUTE_BY_USER_ID = Object.freeze(
  Object.fromEntries(
    Object.entries(PERSONA_TO_USER_ID).map(([personaKey, userId]) => [
      userId,
      PERSONA_DEFAULT_ROUTE[personaKey],
    ]),
  ),
);

const ROLE_FALLBACK_CANDIDATES = Object.freeze([
  [ROLE_IDS.SYSTEM_ADMIN, '/admin/users'],
  [ROLE_IDS.DATA_STEWARD, '/admin/data'],
  [ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER, '/admin/access-requests'],
  [ROLE_IDS.AI_OPERATOR, '/ai/orchestration'],
  [ROLE_IDS.EXECUTIVE_SPONSOR, '/executive'],
  [ROLE_IDS.PORTFOLIO_MANAGER, '/delivery'],
  [ROLE_IDS.PROGRAM_MANAGER, '/delivery'],
  [ROLE_IDS.PROJECT_MANAGER, '/projects'],
  [ROLE_IDS.FINANCIAL_ANALYST, '/financials'],
  [ROLE_IDS.MARKET_RESEARCH_ANALYST, '/market-research'],
  [ROLE_IDS.READONLY_VIEWER, '/home'],
  [ROLE_IDS.CONTRACTOR_ANALYST, '/access/status'],
  [ROLE_IDS.CLINICAL_SME_REVIEWER, '/home'],
  [ROLE_IDS.RESOURCE_MANAGER, '/analytics/resources'],
]);

export function isKnownRoutePath(routePath) {
  return KNOWN_ROUTE_PATHS.has(routePath);
}

// Legacy alias routes that still resolve in the router (via redirect) but must
// never be handed out as a landing destination — a seeded/stored preference
// pointing at an alias resolves to its canonical home instead. This is the
// shared source of truth for the route-alias redirect register (02 §A):
// the web router renders one <LegacyRedirect> per row, and landing-route
// resolution canonicalizes stored preferences through the same table.
//
// Contract (02 §A "universal redirect contract", AC-REDIR-1..5):
// - every alias performs a client redirect to its target, preserving all
//   query parameters and path params (`:id` placeholders are re-substituted);
// - the alias passes through the same route guard as the target (RBAC
//   parity — a user denied at the target is denied identically via the alias);
// - old URLs keep resolving for the life of the product.
//
// Targets may carry a fixed query string (e.g. `?view=capacity`) that the
// redirect merges with the incoming query.
export const LEGACY_ROUTE_ALIASES = Object.freeze({
  '/project-factory/review': '/project-factory',
  '/analytics/loes': '/loe-dashboard',
  '/analytics/resources': '/resources',
  '/admin/report-templates': '/admin/config/report-registry',
  '/exports': '/products',
  '/work-items/:id/log-time': '/work-items/:id?logTime=1',
  '/meetings/:id/convert-to-time': '/meetings/:id?section=time',
  '/resources/scheduler': '/resources?view=capacity',
  '/resources/utilization': '/resources?view=capacity',
  '/analytics/mission-impact': '/executive?view=analytics',
  '/admin/data-integrations': '/admin/data',
});

export function resolveDefaultLandingRoute({
  preferredRoute,
  actorRoles = [],
  actorUserId = '',
  fallbackRoute = '/home',
} = {}) {
  const roleSet = new Set(actorRoles || []);
  const canonicalPreferred = LEGACY_ROUTE_ALIASES[preferredRoute] || preferredRoute;
  const personaFallback = PERSONA_ROUTE_BY_USER_ID[actorUserId] || null;
  const roleFallbacks = ROLE_FALLBACK_CANDIDATES.filter(([roleId]) => roleSet.has(roleId)).map(
    ([, route]) => route,
  );
  const candidates = [
    canonicalPreferred,
    personaFallback,
    ...roleFallbacks,
    fallbackRoute,
    '/home',
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    // Alias targets may carry a fixed query (e.g. `/resources?view=capacity`).
    // Permission/known-route checks run against the bare path; the query is
    // preserved on the returned landing route so the target view opens.
    const [routePath, query = ''] = candidate.split('?');
    if (!isKnownRoutePath(routePath)) continue;
    const requiredRoles = ROUTE_PERMISSIONS[routePath];
    if (requiredRoles === undefined || hasAnyRole(actorRoles, requiredRoles)) {
      return query ? `${routePath}?${query}` : routePath;
    }
  }

  return '/home';
}

export function listRoutes() {
  return ROUTES;
}

export function routesByPersona(_persona, _actorRoles) {
  return ROUTES.filter(
    (r) => r.section !== 'auth' && (ROUTE_PERMISSIONS[r.path] === undefined || true),
  );
}

export const SECTION_LABELS = Object.freeze({
  home: 'Home',
  executive: 'Executive',
  delivery: 'Delivery',
  ai: 'AI',
  admin: 'Admin',
  me: 'Me',
});

// Re-exported for callers without a separate import.
export { ROLE_IDS };
